import {
  File_Audience,
  type FileUploadFragment,
  useFindFileUploadQuery,
  useUpdateFileUploadMutation,
} from "@qw/graphql";
import {
  ButtonGroup,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  FieldInput,
  Form,
  FormReset,
  FormSubmit,
  Group,
  Loading,
  Separator,
  useToast,
} from "@qw/ui";
import type { FC } from "react";
import { z } from "zod";
import { AudienceField, audienceSchema } from "./audience-field";
import { TagSelector } from "./tag-selector";

export interface EditFileDialogProps {
  fileId: string;
  onClose: () => void;
}

export const EditFileDialog = ({ fileId, onClose }: EditFileDialogProps) => {
  const { data, loading } = useFindFileUploadQuery({ variables: { input: { id: fileId } } });

  const file = data?.findFile;

  return (
    <DialogContent className="sm:max-w-md">
      <DialogTitle>Edit file</DialogTitle>

      {loading && !file && <Loading label="Loading file data..." />}

      {!loading && !file && <div>File Not Found</div>}

      {file && <EditFileForm file={file} onClose={onClose} />}
    </DialogContent>
  );
};

export interface EditFileForm {
  file: FileUploadFragment;
  onClose: () => void;
}

export const EditFileForm: FC<EditFileForm> = ({ file, onClose }) => {
  const { toast } = useToast();
  const [update] = useUpdateFileUploadMutation();

  const validationSchema = audienceSchema.extend({
    filename: z.string().min(1, { message: "Invalid name" }),
  });

  const defaultValues = {
    filename: file.filename ?? "",
    audience: (file?.audience as File_Audience) ?? File_Audience.Internal,
  };

  const handleSubmit = async (values: z.infer<typeof validationSchema>) =>
    await update({
      variables: { input: { id: file.id, ...values } },
      onError: (e) => toast({ title: e.message }),
      onCompleted: () => toast({ title: `File updated` }),
      refetchQueries: ["FilesByInsured", "FilesByLabel"],
    });

  return (
    <Form validationSchema={validationSchema} defaultValues={defaultValues} onSubmit={handleSubmit}>
      <Group>
        <DialogHeader>
          <h4>Edit permissions</h4>
          <DialogDescription>Select who should have access to this file</DialogDescription>
          <DialogDescription>
            The default is `All Users`. Please select `Internal Users Only` if you do not want the agent to have access
            to this file.
          </DialogDescription>
        </DialogHeader>
        <AudienceField />

        <Separator />

        <DialogHeader>
          <h4>Add tags</h4>
        </DialogHeader>
        <TagSelector fileId={file.id} />

        <Separator />

        <DialogHeader>
          <h4>Edit filename</h4>
        </DialogHeader>
        <FieldInput name="filename" placeholder="New file name" />

        <ButtonGroup>
          <FormReset onClick={onClose}>Close</FormReset>
          <FormSubmit />
        </ButtonGroup>
      </Group>
    </Form>
  );
};
